import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CarrierChargeType {
  id?: number;
  name: string;
  mode: 'AIR' | 'SEA';
  appliesTo: string[];
  chargeBasis: string;
  remarks: string;
  createdAt?: Date;
  updatedAt?: Date;
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CarrierChargeTypesService {
  private apiUrl = `${environment.apiUrl}/forwarding/carrier-charge-types`;
  private chargeTypes$ = new BehaviorSubject<CarrierChargeType[]>([]);

  constructor(private http: HttpClient) {
    this.loadChargeTypes();
  }

  private loadChargeTypes(): void {
    console.log('[CarrierChargeTypesService] Loading charge types from:', this.apiUrl);
    this.http.get<CarrierChargeType[]>(this.apiUrl).subscribe({
      next: (chargeTypes) => {
        console.log('[CarrierChargeTypesService] Successfully loaded charge types from API:', chargeTypes.length);
        // Convert date strings to Date objects
        const processedChargeTypes = chargeTypes.map(ct => ({
          ...ct,
          createdAt: ct.createdAt ? new Date(ct.createdAt) : undefined,
          updatedAt: ct.updatedAt ? new Date(ct.updatedAt) : undefined
        }));
        this.chargeTypes$.next(processedChargeTypes);
      },
      error: (error) => {
        console.error('[CarrierChargeTypesService] Error loading charge types from API, falling back to mock data:', error);
        this.loadMockData();
      }
    });
  }

  getChargeTypes(): Observable<CarrierChargeType[]> {
    return this.chargeTypes$.asObservable();
  }

  getChargeTypesList(): CarrierChargeType[] {
    return this.chargeTypes$.value;
  }

  addChargeType(chargeType: Omit<CarrierChargeType, 'id' | 'createdAt'>): void {
    this.http.post<CarrierChargeType>(this.apiUrl, chargeType).subscribe({
      next: () => {
        console.log('[CarrierChargeTypesService] Charge type created, reloading');
        this.loadChargeTypes();
      },
      error: (error) => console.error('[CarrierChargeTypesService] Error adding charge type:', error)
    });
  }

  updateChargeType(id: number, chargeType: Omit<CarrierChargeType, 'id' | 'createdAt'>): void {
    this.http.put<CarrierChargeType>(`${this.apiUrl}/${id}`, chargeType).subscribe({
      next: () => {
        console.log('[CarrierChargeTypesService] Charge type updated, reloading');
        this.loadChargeTypes();
      },
      error: (error) => console.error('[CarrierChargeTypesService] Error updating charge type:', error)
    });
  }

  deleteChargeType(id: number): void {
    this.http.delete(`${this.apiUrl}/${id}`).subscribe({
      next: () => {
        console.log('[CarrierChargeTypesService] Charge type deleted, reloading');
        this.loadChargeTypes();
      },
      error: (error) => console.error('[CarrierChargeTypesService] Error deleting charge type:', error)
    });
  }

  private loadMockData(): void {
    const mockChargeTypes: CarrierChargeType[] = [
      { id: 1, name: 'Air Freight', mode: 'AIR', appliesTo: ['AIR'], chargeBasis: 'per kg', remarks: 'Air cargo transportation', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 2, name: 'Fuel Surcharge', mode: 'AIR', appliesTo: ['AIR'], chargeBasis: 'per kg', remarks: 'Aviation fuel adjustment', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 3, name: 'Security Surcharge', mode: 'AIR', appliesTo: ['AIR'], chargeBasis: 'per kg', remarks: 'AMS/ACI/ISPS', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 4, name: 'Arrastre / Wharfage / AISL', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per container', remarks: 'Port handling charges', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 5, name: 'BAF', mode: 'SEA', appliesTo: ['FCL'], chargeBasis: 'per container', remarks: 'Bunker adjustment factor', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 6, name: 'CAF', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per container', remarks: 'Currency adjustment factor', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 7, name: 'Carrier Local Charges', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per container', remarks: 'Includes terminal handling, etc.', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 8, name: 'Container Deposit', mode: 'SEA', appliesTo: ['FCL'], chargeBasis: 'per container', remarks: 'Refundable container deposit', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 9, name: 'DO Fee (Delivery Order)', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per shipment', remarks: 'Delivery order processing', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 10, name: 'Documentation Fee', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per shipment', remarks: 'Bill of lading and documentation', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 11, name: 'Extra Trucking Fee', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per container', remarks: 'Additional trucking beyond standard', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 12, name: 'Ocean Freight', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per container', remarks: 'Quoted directly by shipping line', createdAt: new Date('2025-01-01'), isActive: true },
      { id: 13, name: 'Terminal Handling Charges', mode: 'SEA', appliesTo: ['FCL', 'LCL'], chargeBasis: 'per container', remarks: 'Port terminal operations', createdAt: new Date('2025-01-01'), isActive: true }
    ];

    console.log('[CarrierChargeTypesService] Using mock data:', mockChargeTypes.length, 'items');
    this.chargeTypes$.next(mockChargeTypes);
  }
}
